
export enum WorkOrderStatus {
  PENDING = 'Pendiente',
  IN_PROGRESS = 'En Progreso',
  COMPLETED = 'Finalizado'
}

export enum Priority {
  NORMAL = 'Normal',
  URGENT = 'Urgente'
}

export type UserRole = 'admin' | 'technician';

// Because specialties are now dynamic and configurable, we can't restrict them to a hardcoded union type.
export type TechCategory = string;

export interface NotificationChannels {
  email: boolean;
  whatsapp: boolean;
  sms: boolean;
}

export interface NotificationConfig {
  adminEmail: string;
  adminPhone: string;
  materialChannels: NotificationChannels;
  issueChannels: NotificationChannels;
}

export interface SpecialtyGroup {
  id: string;
  name: string;
  specialties: string[];
}

export interface QuickTemplateGroup {
  id: string;
  name: string;
  specialtyKey?: string;
  enabled: boolean;
  phrases: string[];
}

export interface QuickTemplatesConfig {
  enabled: boolean; // Si se muestran o no en 'Rellenar Trabajo'
  hasUserConfigured?: boolean; // Si el usuario ha tomado una decisión explícita de activación
  groups: QuickTemplateGroup[];
}

export interface CompanySettings {
  id?: string; // Exposed for fallback logic
  name: string;
  notifications: NotificationConfig;
  specialties?: string[]; // Legacy support
  specialtyGroups?: SpecialtyGroup[]; // V25: Groups
  companyLogo?: string | null; // Base64 or URL
  soloMode?: boolean; // V54: One-man company mode
  workDayStart?: string | null; // V105: HH:mm
  workDayEnd?: string | null;   // V105: HH:mm
  subscriptionStatus?: string | null; // V2151: Added for onboarding flow logic
  currentTierId?: string | null;      // V2151
  onboardingCompleted?: boolean; // V2152: Added to prevent permanent onboarding redirect
  quickTemplatesConfig?: QuickTemplatesConfig;
}

export interface SubscriptionTier {
  id: string;
  name: string;
  description: string;
  priceMonthly: number;
  priceYearly: number;
  maxTechnicians: number | null;
  maxOrdersMonthly: number | null;
  maxAiMinutesMonthly: number | null;
  features: string[];
  stripePriceIdMonthly?: string;
  stripePriceIdYearly?: string;
}

export interface Subscription {
  id: string;
  companyId: string;
  stripeCustomerId: string;
  stripeSubscriptionId?: string;
  status: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd: boolean;
}

export interface CompanyInvitation {
  id: string;
  companyId: string;
  email: string;
  role: UserRole;
  token: string;
  expiresAt: string;
  acceptedAt?: string | null;
  createdAt: string;
  companies?: { name: string } | null;
}

export interface Company {
  id: string;
  name: string;
  slug?: string;
  adminEmail: string;
  taxId?: string;
  address?: Record<string, any>;
  ownerUserId?: string;
  currentTierId?: string;
  subscriptionStatus: 'trialing' | 'active' | 'past_due' | 'cancelled' | 'incomplete';
  trialEndsAt?: string;
  settings?: CompanySettings;
  createdAt: string;
  updatedAt: string;
}

export interface SubscriptionSummary {
  companyId: string;
  tierId: string | null;
  tierName: string | null;
  subscriptionStatus: string | null;
  trialEndsAt: string | null;
  maxTechnicians: number | null;
  currentTechnicians: number;
  maxOrdersMonthly: number | null;
  currentOrdersMonthly: number;
}

export interface CreateInvitationInput {
  email: string;
  role?: UserRole;
  companyId?: string;
  name?: string;
  phone?: string;
  category?: string;
  specialties?: string[];
  color?: string;
}

export interface OnboardingCompanyInput {
  name: string;
  taxId?: string;
  address?: Record<string, any>;
}


export interface Location {
  lat: number;
  lng: number;
  address: string;
}

export interface WorkSite {
  id: string;
  companyId?: string; // SaaS Tenant ID
  name: string;
  address?: string; // Formatted address string
  isUniqueAddress?: boolean; // True if this site represents client's unique address / domicilio
  locationDetails?: {
    street?: string | null;
    number?: string | null;
    extra?: string | null;
    zipCode?: string | null;
    city?: string | null;
    province?: string | null;
  };
}

export type LocationDetails = NonNullable<WorkSite['locationDetails']>;

export interface Client {
  id: string;
  companyId?: string; // SaaS Tenant ID
  name: string; // Company Name
  // Removed taxId, address, contactPerson per V22 requirements
  email: string;
  phone: string;
  createdAt: string; // Added for sorting
  workSites?: WorkSite[]; // List of specific work sites/obras linked to client
  activity?: number;
  totalOrders?: number;
}

export interface WorkOrderEvent {
  id: string;
  type: 'incident' | 'material_request' | 'status_change';
  text: string;
  timestamp: string;
  status: 'pending' | 'resolved' | 'completed' | 'info'; // V2233: Flexibilidad para trazabilidad
  technicianName?: string; // Para saber quién hizo el cambio
}

export interface LaborEntry {
  name: string;
  hours: number;
}

export interface LaborJournalEntry {
  id: string;
  date: string;
  mainTechHours: number;
  companions: { name: string; hours: number }[];
  notes: string;
  technicianId: string;
  signature?: string; // V54: Firma de jornada
  isAbsent?: boolean; // V54: Cliente ausente en jornada
  lastExportedAt?: string; // Sello: fecha en la que esta jornada fue incluida en algún PDF exportado
}

export interface Technician {
  id: string;
  authUserId?: string | null; // V37: Links to auth.users, null if pending registration
  companyId?: string;
  name: string;
  email?: string;
  phone?: string;
  category: TechCategory;
  specialties: string[];
  status: 'active' | 'inactive' | 'busy' | 'pending'; // V37: Added 'pending'
  avatar?: string;
  color: string;
  currentLocation?: Location;
  maxActiveOrders?: number;
  currentLoad?: number;
  role?: UserRole;
}
export interface WorkOrder {
  id: string;
  _uuid?: string; // Internal UUID for debugging/integrity
  companyId?: string; // SaaS Tenant ID
  clientName: string;
  clientId?: string;
  // Removed project per V23 request
  title: string; // "Asunto" / Primary descriptor now
  workSiteId?: string; // Link to specific WorkSite ID
  description: string;
  status: WorkOrderStatus;
  assignedTo?: string[]; // IDs of assigned technicians
  hiddenForTechnician?: boolean; // V30.29: Allow Main Tech to hide/archive completed order from their view without Admin archiving it.
  priority: Priority;
  technicianId: string | null;
  technicianNameSnapshot?: string; // Persisted name for deleted technicians
  createdAt: string;
  scheduledDate: string;
  completedAt?: string; // Date when status became COMPLETED
  location: Location;
  materialsUsed: string[];
  markedForArchive?: boolean;
  events?: WorkOrderEvent[]; // Structured storage for Incidents and Material Requests
  attachments?: WorkOrderAttachment[]; // New feature: V40 Subida de adjuntos
  aiSummary?: string;
  actualHours?: number; // Hours for the assigned technician
  additionalLabor?: LaborEntry[]; // Hours for helper technicians
  laborJournal?: LaborJournalEntry[]; // V24: Enhanced Daily Journal
  technicianNotes?: string; // Notes added by the technician (Legacy: use laborJournal notes)
  obra?: string; // Project / Site name (legacy property often used in UI)

  // Archiving logic
  isArchived?: boolean;
  rootOrderId?: string; // V2145: ID of the first order in the chain for tracking total hours
  lastExportedAt?: string; // Sello: fecha de la última exportación parcial de este parte
  deletedForTechnician?: boolean; // V2233: Soft-delete exclusivo para el técnico
  createdByRole?: UserRole; // V4.220: Track authorship for cancel/reject logic
  partNumber?: number | null; // Sequential license-plate-style display number per company
}

export type TechnicianStatus = 'active' | 'inactive' | 'busy';



export interface StatsData {
  totalOrders: number;
  completedToday: number;
  revenueMonth: number;
  efficiency: number;
}

export interface AIAnalysisResult {
  summary: string;
  suggestedMaterials: string[];
  safetyWarnings: string[];
  estimatedTimeHours: number;
}

export interface MaterialItem {
  id: string;
  name: string;
  quantity: number;
  unit?: string;
}

export type SaveStatus = 'saved' | 'saving' | 'error';

export type ModalType = 'materialRequest' | 'issue' | 'materials' | 'preview' | 'signature' | 'attachment' | 'absent' | 'summary' | null;

export interface WorkOrderAttachment {
  id: string;
  workOrderId: string;
  fileName: string;
  filePath?: string;
  fileUrl: string;
  contentType: string;
  size: number;
  uploadedBy?: string;
  createdAt: string;
}

export interface NewOrderData {
  clientMode: 'existing' | 'new' | 'continue';
  clientId: string;
  newClientName: string;
  newClientPhone: string;
  obra: string;
  workSiteId: string;
  isDomicilio: boolean;
  street: string;
  number: string;
  extra: string;
  zipCode: string;
  city: string;
  province: string;
  description: string;
  priority: Priority;
  date: string;
  location: string;
  rootOrderId: string;
  selectedOrderId?: string;
}


